import { useParams, Link } from 'react-router-dom'
import { useApi } from '../hooks/useApi.js'
import { getBlogPost } from '../lib/api.js'
import ErrorFallback from '../components/ErrorFallback.jsx'
import Skeleton from '../components/Skeleton.jsx'
import SharePost from '../components/SharePost.jsx'

function formatDate(dateString) {
  if (!dateString) return ''
  const d = new Date(dateString)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

function BlogDetailPage() {
  const { slug } = useParams()
  const { data, loading, error, refetch } = useApi(() => getBlogPost(slug), [slug])

  if (loading) {
    return (
      <div className="pg-narrow">
        <Skeleton className="h-4 w-48 mb-4" />
        <Skeleton className="h-11 w-3/4 mb-6" />
        <Skeleton className="h-5 w-full mb-8" />
        <Skeleton className="h-4 w-full mb-4" />
        <Skeleton className="h-4 w-5/6 mb-4" />
        <Skeleton className="h-4 w-full mb-4" />
      </div>
    )
  }

  if (error) return <ErrorFallback message="Failed to load post" onRetry={refetch} />

  const post = data
  if (!post) return <ErrorFallback message="Post not found" />

  return (
    <article className="pg-narrow pb-24">
      {/* Header */}
      <header className="mb-6">
        <p className="font-mono text-[13px] text-soft-dim mb-4">
          {formatDate(post.published_at)} · {post.read_time} min
        </p>
        <h1 className="text-[32px] sm:text-[36px] md:text-[44px] font-normal leading-[1.08] tracking-[-1.4px] mb-5">
          {post.title}
        </h1>
        {post.excerpt && (
          <p className="text-[19px] leading-[1.5] text-soft-dim">
            {post.excerpt}
          </p>
        )}
      </header>

      {/* Body */}
      <div
        className="blog-body text-[18px] leading-[1.7] text-soft-ink"
        dangerouslySetInnerHTML={{ __html: post.html_body || post.body }}
      />

      {/* Tags */}
      {post.tag_list?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-soft-rule">
          {post.tag_list.map((tag) => (
            <span key={tag} className="px-2 py-1 bg-soft-hi text-xs text-soft-ink font-mono">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Share */}
      <div className="mt-12 pt-8 border-t border-soft-rule">
        <SharePost title={post.title} />
      </div>

      {/* Back link */}
      <div className="mt-12">
        <Link
          to="/blog"
          className="text-sm text-soft-dim hover:text-soft-ink transition-colors"
        >
          ← All posts
        </Link>
      </div>

      {/* Blog body styles */}
      <style>{`
        .blog-body p {
          margin: 24px 0;
        }
        .blog-body h2 {
          font-size: 28px;
          font-weight: 400;
          line-height: 1.2;
          letter-spacing: -0.5px;
          margin: 48px 0 24px;
          color: var(--soft-ink);
        }
        .blog-body blockquote {
          margin: 32px -8px;
          padding: 12px 16px;
          background: var(--soft-hi);
          font-style: italic;
        }
        .blog-body blockquote p {
          margin: 0;
        }
        .blog-body code {
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-size: 15px;
          background: var(--soft-surface);
          padding: 1px 6px;
          border: 1px solid var(--soft-rule);
        }
        .blog-body pre {
          background: #0f0f0f;
          color: #dcdcdc;
          padding: 16px;
          overflow-x: auto;
          margin: 24px 0;
        }
        .blog-body pre code {
          background: transparent;
          border: none;
          padding: 0;
          font-size: 14px;
        }
        .blog-body ul, .blog-body ol {
          margin: 24px 0;
          padding-left: 24px;
        }
        .blog-body li {
          margin: 8px 0;
        }
        .blog-body a {
          color: var(--soft-dim);
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .blog-body a:hover {
          color: var(--soft-ink);
        }
        .blog-body img {
          max-width: 100%;
          height: auto;
          margin: 32px 0;
        }
        .blog-body table {
          width: 100%;
          border-collapse: collapse;
          margin: 24px 0;
          font-size: 15px;
        }
        .blog-body th,
        .blog-body td {
          border: 1px solid var(--soft-rule);
          padding: 8px 12px;
          text-align: left;
        }
        .blog-body th {
          background: var(--soft-hi);
        }
      `}</style>
    </article>
  )
}

export default BlogDetailPage
